'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Link from 'next/link';
import { ensureProtocol } from '../../../lib/ensureProtocol';

interface Bot {
  slug: string;
  name: string;
  description: string | null;
}

interface SamplesContentProps {
  locale: string;
  bots: Bot[];
}

export function SamplesContent({ locale, bots }: SamplesContentProps) {
  const t = useTranslations('consumer.samples');
  const creatorUrl = ensureProtocol(process.env.NEXT_PUBLIC_CREATOR_URL || 'https://boty.app');

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
          {t('title')}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 500, mx: 'auto' }}
        >
          {t('subtitle')}
        </Typography>
      </Box>

      {/* Bot Cards Grid */}
      <Grid container spacing={3}>
        {bots.map((bot) => (
          <Grid key={bot.slug} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {bot.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bot.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  LinkComponent={Link}
                  href={`/${locale}/${bot.slug}`}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {t('tryBot')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* CTA Section */}
      <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 8 } }}>
        <Typography variant="h5" gutterBottom>
          {t('createPrompt')}
        </Typography>
        <Button
          LinkComponent={Link}
          href={creatorUrl}
          variant="contained"
          size="large"
          sx={{ mt: 2 }}
        >
          {t('createCta')}
        </Button>
      </Box>
    </Container>
  );
}
